import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { fmtPeriod } from "@/lib/period";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
const chf = (v) => Number(v || 0).toFixed(2);

// Iter311: Gebuchten Lohnlauf korrigieren → Korrekturlauf mit Differenzbuchung
export function PayrollCorrectionDialog({ run, companyId, employee, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const b = run?.breakdown || {};
  const hourly = b.employment_type === "hourly";

  useEffect(() => {
    if (!run) return setF(null);
    setF({
      base_salary: !hourly && b.monthly_salary ? String(b.monthly_salary) : "",
      hours_worked: hourly && b.hours_worked ? String(b.hours_worked) : "",
      hourly_rate: hourly && b.hourly_rate ? String(b.hourly_rate) : "",
      bonus_amount: b.bonus_amount ? String(b.bonus_amount) : "",
      allowance_tax_free: b.allowance_tax_free ? String(b.allowance_tax_free) : "",
      manual_deduction: b.manual_deduction ? String(b.manual_deduction) : "",
      unpaid_days: b.unpaid_absence?.days ? String(b.unpaid_absence.days) : "",
      payment_date: new Date().toISOString().slice(0, 10),
      reason: "",
    });
  }, [run?._id]);

  if (!run || !f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.reason.trim()) { toast.error("Bitte einen Korrekturgrund angeben."); return; }
    setSaving(true);
    try {
      const payload = {
        reason: f.reason.trim(),
        payment_date: f.payment_date || null,
        bonus_amount: num(f.bonus_amount) ?? 0,
        allowance_tax_free: num(f.allowance_tax_free) ?? 0,
        manual_deduction: num(f.manual_deduction) ?? 0,
        unpaid_days: f.unpaid_days === "" ? null : num(f.unpaid_days),
      };
      if (hourly) { payload.hours_worked = num(f.hours_worked); payload.hourly_rate = num(f.hourly_rate); }
      else payload.base_salary = num(f.base_salary);
      const r = await apiClient.post(`/companies/${companyId}/payroll-runs/${run._id}/correct`, payload);
      const d = r.data.net_salary;
      toast.success(`Korrekturlauf ${fmtPeriod(run.period)} erstellt – Netto-Differenz CHF ${chf(d)} ${d < 0 ? "(Rückforderung)" : "(Nachzahlung)"}. Jetzt prüfen und buchen.`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Korrektur fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payroll-correction-dialog">
        <DialogHeader>
          <DialogTitle>Lohnkorrektur – {employee ? `${employee.first_name} ${employee.last_name}, ` : ""}{fmtPeriod(run.period)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="payroll-correction-hint">
            Der gebuchte Lohnlauf (Netto CHF {chf(run.net_salary)}) bleibt unverändert. Es entsteht ein Korrekturlauf, der nur die
            <b> Differenz</b> ausweist und bucht – negativer Betrag = Rückforderung gegenüber dem Mitarbeiter.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {hourly ? (
              <>
                <div className="space-y-1">
                  <Label>Stunden (korrigiert)</Label>
                  <Input type="number" step="0.25" value={f.hours_worked} onChange={set("hours_worked")} data-testid="correction-hours-input" />
                </div>
                <div className="space-y-1">
                  <Label>Stundenansatz (CHF/h)</Label>
                  <Input type="number" step="0.05" value={f.hourly_rate} onChange={set("hourly_rate")} data-testid="correction-hourly-rate-input" />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>Monatslohn (korrigiert)</Label>
                  <Input type="number" step="0.05" value={f.base_salary} onChange={set("base_salary")} data-testid="correction-base-salary-input" />
                </div>
                <div className="space-y-1">
                  <Label>Unbezahlter Urlaub (Kalendertage)</Label>
                  <Input type="number" step="1" min="0" value={f.unpaid_days} onChange={set("unpaid_days")} placeholder="leer = aus Absenzen" data-testid="correction-unpaid-days-input" />
                </div>
              </>
            )}
            <div className="space-y-1">
              <Label>Bonus / Prämie (AHV-pflichtig)</Label>
              <Input type="number" step="0.05" value={f.bonus_amount} onChange={set("bonus_amount")} data-testid="correction-bonus-input" />
            </div>
            <div className="space-y-1">
              <Label>Spesen / Zulage (AHV-frei)</Label>
              <Input type="number" step="0.05" value={f.allowance_tax_free} onChange={set("allowance_tax_free")} data-testid="correction-allowance-input" />
            </div>
            <div className="space-y-1">
              <Label>Manueller Abzug (vom Netto)</Label>
              <Input type="number" step="0.05" value={f.manual_deduction} onChange={set("manual_deduction")} data-testid="correction-deduction-input" />
            </div>
            <div className="space-y-1">
              <Label>Buchungs-/Zahlungsdatum</Label>
              <SwissDateField  value={f.payment_date} onChange={set("payment_date")} data-testid="correction-payment-date-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Korrekturgrund *</Label>
            <Input value={f.reason} onChange={set("reason")} placeholder="z.B. Lohnerhöhung rückwirkend, unbezahlter Urlaub nachträglich gemeldet" data-testid="correction-reason-input" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="correction-cancel-button">Abbrechen</Button>
            <Button size="sm" className="bg-slate-900 text-white" onClick={save} disabled={saving} data-testid="correction-save-button">
              {saving ? "Berechne…" : "Korrekturlauf erstellen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
